/**
 * «Активность → Автоакцепт» — ровно то, что отдают `GET /api/activity/accept/period` и
 * `GET /api/activity/accept/day` (право `accept_view`). Спека
 * `docs/specs/2026-11-05-activity-autoaccept-design.md`; числа — те же, что у `prokat-runner accept-review`.
 */
import { ApiFailure, apiFetch, query } from "@/lib/api";

export type Rule = "full" | "stylist" | "none";
export type Verdict =
  | "auto"
  | "partial"
  | "same"
  | "diff"
  | "no_statuses"
  | "no_leave"
  | "auto_leave"
  | "open"
  | "no_rule";
/** Почему сегмент «требует решения»: простой (с комментарием рядом или без) или флаг всего дня. */
export type Reason = "idle" | "idle_commented" | "no_statuses" | "no_leave" | "auto_leave" | "open" | "no_rule";
export type SegmentKind = "auto" | "manual";

export type Segment = {
  from: string;
  to: string;
  minutes: number;
  kind: SegmentKind;
  reason: Reason | null;
  in_crm: boolean;
  hint: string | null;
};

/** Отрезок миниатюры шкалы: время «ЧЧ:ММ:СС» или ISO, вид и причина. */
export type BarSegment = { from: string; to: string; kind: SegmentKind; reason: Reason | null };
export type Span = { from: string; to: string };

export type AcceptedRow = {
  id: number | null;
  from: string;
  to: string;
  minutes: number;
  by_id: number | null;
  by_name: string | null;
  by_robot: boolean;
  category: number | null;
};

type DayBase = {
  user: { id: number; name: string };
  location: string | null;
  role: number;
  rule: Rule;
  date: string;
  verdict: Verdict;
  paid_min: number;
  auto_min: number;
  auto_pending_min: number;
  manual_min: number;
  manual_count: number;
  accepted_min: number;
  accepted_by: "robot" | "hr" | "mixed" | null;
  leave: string | null;
};

/** День в таблице периода: без подробностей, только то, что рисует миниатюра шкалы. */
export type AcceptDay = DayBase & { bar: { segments: BarSegment[]; accepted: Span[] } };
/** День целиком — на странице смены. */
export type AcceptDayFull = DayBase & { segments: Segment[]; accepted: AcceptedRow[] };

export type AcceptTotals = {
  shifts: number;
  paid_min: number;
  auto_min: number;
  auto_pending_min: number;
  manual_min: number;
  manual_count: number;
  accepted_min: number;
  robot_days: number;
  hr_days: number;
};

export type AcceptPerson = {
  user: { id: number; name: string };
  location: string | null;
  location_id: number | null;
  role: number;
  rule: Rule;
  totals: AcceptTotals;
  days: AcceptDay[];
};

export type AcceptPeriod = {
  from: string;
  to: string;
  location: { id: number; name: string } | null;
  rule: Rule | null;
  locations: { id: number; name: string }[];
  people: AcceptPerson[];
  totals: AcceptTotals;
  summary: Record<Verdict, number>;
};

export type AcceptStatus = { status_id: number; name: string; from: string; to: string; paid: boolean; minutes: number };

export type AcceptAction = {
  id: number;
  at: string;
  name: string;
  /** Минут от прошлого следа работы (края смены и перерывов — тоже следы); у смен статуса — null. */
  gap_min: number | null;
  is_status: boolean;
  /** След работы: не смена статуса и не «Работа с клиентом» (её ставит выгрузка). */
  is_trace: boolean;
  comment: string | null;
  tasks: string | null;
  serial: number | null;
  move_task_id: number | null;
  customer_id: number | null;
  /** Индекс сегмента дня, в который попало действие. */
  segment: number | null;
};

export type AcceptDayDetail = {
  day: AcceptDayFull;
  statuses: AcceptStatus[];
  actions: AcceptAction[];
  billed: number | null;
  links: { reserve: string; reserve_time: string; shift: string };
  rule_label: string;
  rule_text: string;
};

/** Вердикт дня: подпись, плашка (`tone`), квадрат полоски дней (`dot`) и что он значит. */
export const VERDICT: Record<Verdict, { label: string; tone: string; dot: string; hint: string }> = {
  auto: { label: "Пройдёт", tone: "bg-ok-bg text-ok", dot: "bg-ok", hint: "окон нет — автоакцепт примет весь день" },
  partial: { label: "Частично", tone: "bg-warn-bg text-warn", dot: "bg-warn", hint: "есть окна простоя — их решает HR" },
  same: { label: "Принято", tone: "bg-ok-bg text-ok", dot: "bg-ok", hint: "акцепт в CRM уже стоит и совпадает с автоакцептом до 10 минут" },
  diff: { label: "Расходится", tone: "bg-warn-bg text-warn", dot: "bg-warn", hint: "акцепт в CRM уже стоит и расходится с автоакцептом больше чем на 10 минут" },
  no_statuses: { label: "Нет статусов", tone: "bg-err-bg text-err", dot: "bg-err", hint: "нет статусов — ни минуты в оплачиваемых статусах" },
  no_leave: { label: "Нет «Ушел»", tone: "bg-err-bg text-err", dot: "bg-err", hint: "нет «Ушел» — конца смены не видно" },
  auto_leave: { label: "«Ушел» от CRM", tone: "bg-err-bg text-err", dot: "bg-err", hint: "«Ушел» поставила CRM по IP — сотрудник забыл уйти" },
  open: { label: "День идёт", tone: "bg-neutral-bg text-fg-muted", dot: "bg-fg-faint", hint: "день ещё идёт" },
  no_rule: { label: "Без правила", tone: "bg-neutral-bg text-fg-muted", dot: "bg-fg-faint", hint: "роль без правила — всё время решает HR" },
};
export const VERDICTS = Object.keys(VERDICT) as Verdict[];

export const RULE_LABEL: Record<Rule, string> = {
  full: "Полный акцепт",
  stylist: "Стилисты без группы",
  none: "Без правила",
};
export const RULES = Object.keys(RULE_LABEL) as Rule[];

export const ACCEPTED_BY: Record<"robot" | "hr" | "mixed", string> = { robot: "Робот", hr: "HR", mixed: "Робот и HR" };

/** Все салоны — не длиннее стольких дней (сервер отвечает 422 `PERIOD_TOO_LONG`). */
export const ALL_SALONS_MAX_DAYS = 14;

/** Комментарий без текста: сервер отдаёт вместо текста название строки. */
const БЕЗ_ТЕКСТА = ["Добавили комментарий", "Коммент.Старт"];

/** Почему сегмент «требует решения» — словами для HR. */
export function reasonText(s: Pick<Segment, "reason" | "minutes" | "hint">): string {
  const m = Math.round(s.minutes);
  if (s.reason === "idle") return `простой ${m} мин, без комментария`;
  if (s.reason === "idle_commented")
    return БЕЗ_ТЕКСТА.includes(s.hint ?? "")
      ? `простой ${m} мин, рядом «${s.hint}» без текста`
      : `простой ${m} мин, рядом комментарий: «${s.hint ?? ""}»`;
  return s.reason ? VERDICT[s.reason].hint : "";
}

export function loadAcceptPeriod(p: {
  from: string;
  to: string;
  location?: string;
  user?: string;
  rule?: string;
}): Promise<AcceptPeriod> {
  return apiFetch<AcceptPeriod>(`/api/activity/accept/period${query(p)}`);
}

export function loadAcceptDay(user: number, date: string): Promise<AcceptDayDetail> {
  return apiFetch<AcceptDayDetail>(`/api/activity/accept/day${query({ user, date })}`);
}

/** Отказ сервера (4xx: нет права, не салон, период длинный) повтором не лечится — показываем сразу. */
export const retryServerErrors = (n: number, e: Error) => !(e instanceof ApiFailure && e.status < 500) && n < 2;
